// Step 5: Assistance Form - Dynamic form based on selected assistance type
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useVisitWizard } from '../../../../contexts/VisitWizardContext'
import { useCreateVisitTransaction } from '../../../../hooks/useVisits'
import { getTransactionTypeId } from '../../../../constants/assistance-types'
import { useCourts, useOfficeCodes } from '../../../../hooks/useCourts'
import { Button } from '../../../../components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../components/ui/form'
import { Input } from '../../../../components/ui/input'
import { Textarea } from '../../../../components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select'
import { DatePickerField } from '../../../../components/ui/date-picker'
import { RadioGroup, RadioGroupItem } from '../../../../components/ui/radio-group'
import { toast } from 'sonner'
import { ASSISTANCE_TYPE_LABELS } from '../../../../types/visit-wizard'
import { AssistanceDetailFields } from '../../../../components/forms/assistance'

export function AssistanceForm() {
  const { state, addAssistance, goToStep } = useVisitWizard()
  const currentType = state.currentAssistanceType!
  const [isSaving, setIsSaving] = useState(false)
  const [caseType, setCaseType] = useState<string>('')
  const createTransaction = useCreateVisitTransaction()

  // Fetch court and office data
  const { data: courts = [], isLoading: courtsLoading } = useCourts()
  const { data: officeCodes = [], isLoading: officeCodesLoading } = useOfficeCodes()

  const form = useForm({
    defaultValues: {
      // Common fields
      action_taken: '',
      lawyer_assigned: '',
      notes: '',
      date_provided: new Date().toISOString().split('T')[0],
      // Type-specific fields
      document_type: '',
      number_of_oaths: '',
      jail_name: '',
      inmate_name: '',
      case_number: '',
      court_name: '',
      prosecutor_office: '',
      company_name: '',
      issue_description: '',
      mediation_result: '',
    },
  })

  const handleSaveAndAddAnother = async (data: any) => {
    await saveAssistance(data)
    // Go back to assistance selection
    goToStep('assistance-selection')
  }

  const handleSaveAndFinalize = async (data: any) => {
    await saveAssistance(data)
    // Go to summary
    goToStep('summary')
  }

  const saveAssistance = async (data: any) => {
    setIsSaving(true)

    try {
      // Validate visit ID exists
      if (!state.visitId) {
        throw new Error('No visit ID found. Please complete classification first.')
      }

      // Get transaction type ID for this assistance type
      const transactionTypeId = getTransactionTypeId(currentType)

      // Save to database via API
      const savedTransaction = await createTransaction.mutateAsync({
        visit_id: state.visitId,
        transaction_type_id: transactionTypeId,
        transaction_detail: data, // Form data as JSONB
      })

      // Create assistance object with real control number from backend
      const assistance = {
        id: savedTransaction.id.toString(), // Use real database ID
        type: currentType,
        data: data,
        is_saved: true,
        control_number: savedTransaction.control_number ?? undefined,
      }

      // Add to wizard state
      addAssistance(assistance)

      toast.success(`${ASSISTANCE_TYPE_LABELS[currentType]} added successfully`)
      form.reset()
    } catch (error: any) {
      console.error('Failed to save assistance:', error)
      toast.error(error.message || 'Failed to save assistance')
    } finally {
      setIsSaving(false)
    }
  }

  // Check if case type selection is needed
  const needsCaseTypeSelection =
    currentType === 'representation-court' || currentType === 'representation-appellate'

  // Render case type selection for representation forms
  const renderCaseTypeSelection = () => {
    if (currentType === 'representation-court') {
      return (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">Select Case Type</h3>
          <RadioGroup value={caseType} onValueChange={setCaseType}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="civil_case" id="civil" />
              <label htmlFor="civil" className="text-base font-medium cursor-pointer">
                Civil Case
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="criminal_case" id="criminal" />
              <label htmlFor="criminal" className="text-base font-medium cursor-pointer">
                Criminal Case
              </label>
            </div>
          </RadioGroup>
        </div>
      )
    }

    if (currentType === 'representation-appellate') {
      return (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">Select Case Type</h3>
          <RadioGroup value={caseType} onValueChange={setCaseType}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="civil_case_appellate" id="civil-app" />
              <label htmlFor="civil-app" className="text-base font-medium cursor-pointer">
                Civil Case (Appellate)
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="criminal_case_appellate" id="criminal-app" />
              <label htmlFor="criminal-app" className="text-base font-medium cursor-pointer">
                Criminal Case (Appellate)
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="labor_case_appellate" id="labor-app" />
              <label htmlFor="labor-app" className="text-base font-medium cursor-pointer">
                Labor Case (Appellate)
              </label>
            </div>
          </RadioGroup>
        </div>
      )
    }

    return null
  }

  // Render form fields based on assistance type
  const renderTypeSpecificFields = () => {
    // For representation forms, don't show form until case type is selected
    if (needsCaseTypeSelection && !caseType) {
      return renderCaseTypeSelection()
    }

    // Prepare courts data for forms
    const courtsData = courts.map((c) => ({ court: c.court, code: c.code }))

    return (
      <AssistanceDetailFields
        assistanceType={currentType}
        form={form}
        courts={courtsData}
        officeCodes={officeCodes}
      />
    )
  }

  // Show loading state while fetching data
  if (courtsLoading || officeCodesLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center space-y-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-sm text-muted-foreground">Loading form data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-primary mb-2">
          {ASSISTANCE_TYPE_LABELS[currentType]}
        </h2>
        <p className="text-sm text-muted-foreground">
          Fill in the details for this assistance
        </p>
      </div>

      {/* Form */}
      <Form {...form}>
        <form className="space-y-6 max-w-5xl mx-auto">
          {/* Type-Specific Fields */}
          <div className="space-y-4 p-6 border rounded-lg bg-white">
            {renderTypeSpecificFields()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => goToStep('assistance-selection')}
            >
              Back
            </Button>

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={form.handleSubmit(handleSaveAndAddAnother)}
                disabled={isSaving}
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-white"
              >
                Save & Add Another
              </Button>

              <Button
                type="button"
                onClick={form.handleSubmit(handleSaveAndFinalize)}
                disabled={isSaving}
                className="bg-primary hover:bg-primary/90"
              >
                Save & Finalize
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  )
}
